/**
 * Client-side mirror of the backend's `ExtensionPackageAllowedOriginsCodec`
 * (workgroup/src/silos/tableau-server/libraries/tab-domain-packages/.../settings/ExtensionPackageAllowedOriginsCodec.java).
 * Splits/serializes the `extensionPackageAllowedOrigins` site setting value and
 * canonicalizes individual `https://host[:port]` origin tokens.
 *
 * This is a browser-native reimplementation, not a byte-for-byte port: it uses the
 * platform `URL` API for scheme/host/port normalization instead of hand-rolled
 * IDNA/IPv4/IPv6 parsing, per the "one browser-compatible origin algorithm" intent in
 * analysis/w-23836064-allowedorigins-site-setting-phase1.md. Known divergences from the
 * Java codec:
 *  - The WHATWG URL host parser accepts a wider range of IPv4-literal spellings (hex,
 *    octal, non-dotted forms) than the Java codec's strict dotted-decimal rule.
 *  - IPv6 literal compression follows the WHATWG serializer, which is not guaranteed
 *    byte-identical to RFC 5952 in every edge case.
 * Neither divergence is a security boundary here -- this module only powers a
 * best-effort, client-side compliance *preview* (see PackageComplianceEvaluator.ts).
 * The server-side codec and ExtensionPackageAllowedOriginsValidator remain the sole
 * source of truth and reject anything that isn't in their canonical form.
 */

const SEPARATOR = ' ';
// Mirrors the Java codec's HTTPS_SCHEME: https is the only accepted scheme.
const HTTPS_SCHEME = 'https';

// Mirrors the Java codec's DISALLOWED_CHARACTERS: whitespace, ASCII control characters
// (0x00-0x1F, 0x7F), and the CSP metacharacters ';' (directive separator) and
// '"' / "'" (keyword-source quoting).
const DISALLOWED_CHARACTERS_PATTERN = '[\\s\\x00-\\x1F\\x7F;\'"]';
const DISALLOWED_CHARACTERS = new RegExp(DISALLOWED_CHARACTERS_PATTERN);

// Mirrors the Java codec's isBidiControl: LRM/RLM (U+200E-U+200F), LRE/RLE/PDF/LRO/RLO
// (U+202A-U+202E), and the LRI/RLI/FSI/PDI isolate controls (U+2066-U+2069).
const BIDI_CONTROL_PATTERN = '[\\u200E\\u200F\\u202A-\\u202E\\u2066-\\u2069]';
const BIDI_CONTROL = new RegExp(BIDI_CONTROL_PATTERN);

export class OriginSyntaxError extends Error {
  public constructor(public readonly token: string, reason: string) {
    super(`Invalid origin '${token}': ${reason}`);
    // Required for `instanceof OriginSyntaxError` to work when TS downlevels this class to
    // ES5: the compiled `super(...)` call (Error.call(this, ...)) returns a fresh
    // Error-prototyped object rather than mutating `this`, so without this the constructed
    // instance's prototype silently reverts to Error.prototype.
    Object.setPrototypeOf(this, OriginSyntaxError.prototype);
    this.name = 'OriginSyntaxError';
  }
}

export class OriginCanonicalizer {

  private constructor() {}

  /**
   * Splits a stored setting value on its separator. Does not validate or canonicalize
   * the resulting tokens -- callers that need canonical form must still call
   * {@link canonicalizeOrigin} on each token.
   */
  public static splitOrigins(value: string): string[] {
    return value === '' ? [] : value.split(SEPARATOR);
  }

  /**
   * Serializes a collection of already-canonical origins into the stored value form:
   * deduplicated, ASCII-lexicographically sorted, space-joined.
   */
  public static serialize(canonicalOrigins: Iterable<string>): string {
    return Array.from(new Set(canonicalOrigins)).sort().join(SEPARATOR);
  }

  /**
   * Parses `origin` as `https://host[:port]` and returns its canonical serialization:
   * lowercase scheme, a normalized lowercase host (IDNA-encoded DNS name, bracketed IPv6
   * literal, or dotted-decimal IPv4 literal), and the port omitted when it is 443. No path,
   * query, fragment, or user information is permitted, and no scheme other than `https` is
   * accepted -- http and every other scheme are rejected outright.
   *
   * @throws OriginSyntaxError if `origin` is not a well-formed, unambiguous origin.
   */
  public static canonicalizeOrigin(origin: string): string {
    OriginCanonicalizer.rejectDisallowedCharacters(origin);

    const schemeEnd = origin.indexOf('://');
    if (schemeEnd <= 0) {
      throw new OriginSyntaxError(origin, "missing '://' scheme separator");
    }
    if (origin.substring(0, schemeEnd).toLowerCase() !== HTTPS_SCHEME) {
      throw new OriginSyntaxError(origin, "scheme must be 'https'");
    }

    let url: URL;
    try {
      url = new URL(origin);
    } catch {
      throw new OriginSyntaxError(origin, 'not a well-formed absolute URL');
    }

    if (url.username !== '' || url.password !== '') {
      throw new OriginSyntaxError(origin, 'user information is not allowed in an origin');
    }
    if (url.pathname !== '/' || url.search !== '' || url.hash !== '') {
      throw new OriginSyntaxError(origin, 'path, query, and fragment are not allowed in an origin');
    }

    return url.origin;
  }

  /** Like {@link canonicalizeOrigin}, but returns `undefined` instead of throwing. */
  public static tryCanonicalizeOrigin(origin: string): string | undefined {
    try {
      return OriginCanonicalizer.canonicalizeOrigin(origin);
    } catch {
      return undefined;
    }
  }

  /**
   * Extracts the origin (`scheme://host[:port]`) from an absolute URL that may also
   * carry a path/query/fragment, then canonicalizes it via {@link canonicalizeOrigin}.
   * Used for extension `sourceUrl` values, which are full URLs rather than bare origins.
   */
  public static tryCanonicalizeOriginOfUrl(url: string): string | undefined {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return undefined;
    }
    // Preserve userinfo in the reconstructed candidate (rather than stripping it) so that
    // canonicalizeOrigin's user-information check rejects it below, mirroring the Java
    // PackageOriginExtractor/ExtensionPackageAllowedOriginsCodec, which builds its candidate
    // from the full authority (including userinfo) and lets canonicalization reject it.
    const authority = parsed.username !== '' || parsed.password !== ''
      ? `${parsed.username}:${parsed.password}@${parsed.host}`
      : parsed.host;
    return OriginCanonicalizer.tryCanonicalizeOrigin(`${parsed.protocol}//${authority}`);
  }

  private static rejectDisallowedCharacters(token: string): void {
    if (token === '') {
      throw new OriginSyntaxError(token, 'empty origin token');
    }
    if (token.indexOf('*') >= 0) {
      throw new OriginSyntaxError(token, 'wildcard origins are not supported');
    }
    if (DISALLOWED_CHARACTERS.test(token) || BIDI_CONTROL.test(token)) {
      throw new OriginSyntaxError(token, 'contains whitespace, a control character, a CSP metacharacter, or a bidi control character');
    }
  }
}
